import { makeAutoObservable, runInAction, computed } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { userService } from "../services";
import {
  createCreateForm,
  createEditForm,
  createListFilters,
  createListSort,
  createMeta,
  normalizePermissionOverrides,
  resolvePayload,
  resolveRelativeTime,
} from "./user-store.helpers";

const DEFAULT_PAGE_SIZE = 10;

class UserStore {
  // List state
  users = [];
  usersTotal = 0;
  usersLoading = false;
  usersError = "";
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  filters = createListFilters();
  sort = createListSort();

  // Detail state
  usersById = new Map();
  loadingById = new Map();
  savingById = new Map();
  errorById = new Map();
  editFormsById = new Map();
  editMetaById = new Map();

  // Create form state
  createForm = createCreateForm();
  createLoading = false;
  createError = "";

  // Follow-up state
  followUpUserId = null;
  deleteLoading = false;

  // Can create user check
  canCreateUser = false;
  canCreateUserMessage = "";
  canCreateUserError = "";

  // Request management (simplified)
  _abortController = null;

  constructor() {
    makeAutoObservable(this, {
      hasUsers: computed,
      totalPages: computed,
      getUser: false,
      getEditForm: false,
      getEditMeta: false,
      isLoading: false,
      isSaving: false,
      getError: false,
      loadManagedUser: false,
      updateManagedUser: false,
      createManagedUser: false,
    });
  }

  // Computed properties
  get hasUsers() {
    return this.users.length > 0;
  }

  get totalPages() {
    return Math.ceil(this.usersTotal / this.pageSize);
  }

  // List management
  setFilters = (filters) => {
    this.filters = { ...this.filters, ...filters };
  };

  resetFilters = () => {
    this.filters = createListFilters();
  };

  setPage = (page) => {
    this.page = page;
  };

  setPageSize = (size) => {
    this.pageSize = size;
  };

  setSort = (sort) => {
    this.sort = { ...this.sort, ...sort };
  };

  listParams = () => ({
    username: this.filters.username,
    email: this.filters.email,
    company: this.filters.company,
    sortKey: this.sort.key,
    sortDirection: this.sort.direction,
    page: this.page,
    limit: this.pageSize,
  });

  mapUserFromApi = (item) => {
    const lastActiveAgo =
      item.lastActiveAgo ||
      resolveRelativeTime(
        item.lastActive || item.lastActiveAt || item.lastActiveOn,
      );

    return {
      id: item.id || item._id || "",
      userId: item.userId || item.id || item._id || "",
      createdBy: item.createdBy || "",
      displayName: item.displayName || item.name || "",
      username: item.username || item.name || "",
      email: item.email || "",
      role: item.role || "",
      company: item.company || "",
      organizationUnitId: item.organizationUnitId || "",
      organizationUnitName: item.organizationUnitName || "",
      onboardingPct: item.onboardingPct || 0,
      companyId:
        item.companyId ||
        item.company?.id ||
        item.company?._id ||
        item.company?.companyId ||
        item.companyDetails?.id ||
        item.companyDetails?._id ||
        item.companyDetails?.companyId ||
        "",
      incharge: item.incharge,
      projects: item.projects,
      onlineStatus: item.onlineStatus || "offline",
      lastActiveAgo,
      isNew: Boolean(item.isNew),
      randomPass: item.randomPass || null,
      permissionOverrides: normalizePermissionOverrides(
        item.permissionOverrides,
      ),
      effectivePermissions: Array.isArray(item.effectivePermissions)
        ? item.effectivePermissions
        : Array.isArray(item.permissions)
          ? item.permissions
          : [],
      status: item.status || "",
    };
  };

  loadUsers = async () => {
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    runInAction(() => {
      this.usersLoading = true;
      this.usersError = "";
    });

    try {
      const params = this.listParams();
      const data = await userService.listUsers(params, {
        signal: this._abortController.signal,
        throwOnError: false,
      });

      if (!data) {
        runInAction(() => {
          this.users = [];
          this.usersTotal = 0;
          this.usersError = "Unable to load users.";
        });
        return;
      }

      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload?.data?.items)
          ? payload.data.items
          : [];
      const pagination = payload.pagination || payload?.data?.pagination || {};
      const mappedUsers = items.map((item) => this.mapUserFromApi(item));

      runInAction(() => {
        this.users = mappedUsers;
        this.usersTotal = Number.isFinite(pagination.total)
          ? pagination.total
          : mappedUsers.length;
        if (pagination.page && pagination.page !== this.page) {
          this.page = pagination.page;
        }
        if (pagination.limit && pagination.limit !== this.pageSize) {
          this.pageSize = pagination.limit;
        }
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        runInAction(() => {
          this.users = [];
          this.usersTotal = 0;
          this.usersError = "Unable to load users.";
        });
      }
    } finally {
      runInAction(() => {
        this.usersLoading = false;
      });
      this._abortController = null;
    }
  };

  updateOnlineUserStatus = (userId, status, lastActive) => {
    const normalizedId = userId ? String(userId) : "";
    if (!normalizedId) return;
    this.users = this.users.map((user) => {
      if (String(user.id) !== normalizedId) return user;

      return {
        ...user,
        onlineStatus: status,
        lastActiveAgo:
          status === "offline" && lastActive
            ? resolveRelativeTime(lastActive)
            : null,
      };
    });
  };

  checkCanCreateUser = async (companyId) => {
    if (!companyId) {
      return false;
    }
    try {
      const data = await userService.checkCanCreateUser(companyId, {
        throwOnError: false,
      });
      if (!data) {
        throw new Error("Error checking organization units and roles.");
      }

      const payload = resolvePayload(data) || {};
      runInAction(() => {
        this.canCreateUser = Boolean(payload.canCreate);
        this.canCreateUserMessage = payload.message || "";
      });

      return this.canCreateUser;
    } catch (error) {
      const message =
        error?.message || "Error checking organization units and roles";
      runInAction(() => {
        this.canCreateUser = false;
        this.canCreateUserError = message;
      });
      return false;
    }
  };

  // Detail management
  getUser = (userId) => this.usersById.get(userId) ?? null;

  getEditForm = (userId) => this.editFormsById.get(userId) ?? createEditForm();

  getEditMeta = (userId) => this.editMetaById.get(userId) ?? createMeta();

  isLoading = (userId) => this.loadingById.get(userId) ?? false;

  isSaving = (userId) => this.savingById.get(userId) ?? false;

  getError = (userId) => this.errorById.get(userId) ?? "";

  setCreateField = (field, value) => {
    this.createForm = { ...this.createForm, [field]: value };
  };

  setCreatePermissionOverrides = (overrides) => {
    this.createForm = {
      ...this.createForm,
      permissionOverrides: normalizePermissionOverrides(overrides),
    };
  };

  resetCreateForm = () => {
    this.createForm = createCreateForm();
    this.createError = "";
  };

  updateEditField = (userId, field, value) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, { ...current, [field]: value });
  };

  setEditPermissionOverrides = (userId, overrides) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, {
      ...current,
      permissionOverrides: normalizePermissionOverrides(overrides),
    });
  };

  setEditFields = (userId, values = {}) => {
    if (!userId) return;
    const current = this.getEditForm(userId);
    this.editFormsById.set(userId, { ...current, ...values });
  };

  applyUserToForm = (userId, user) => {
    if (!userId || !user) return;
    this.editFormsById.set(userId, {
      name: user.displayName || user.name || "",
      username: user.username || "",
      email: user.email || "",
      role: user.role || "",
      status: user.status || "",
      organizationUnitId: user.organizationUnitId || "",
      organizationUnitName: user.organizationUnitName || "",
      companyId:
        user.companyId ||
        user.company?.id ||
        user.company?._id ||
        user.company?.companyId ||
        user.companyDetails?.id ||
        user.companyDetails?._id ||
        user.companyDetails?.companyId ||
        "",
      permissionOverrides: normalizePermissionOverrides(
        user.permissionOverrides,
      ),
    });
    this.editMetaById.set(userId, {
      company: user.company || "",
      createdAt: user.createdAt || null,
      updatedAt: user.updatedAt || null,
    });
  };

  invalidateUser = (userId) => {
    if (!userId) return;
    this.usersById.delete(userId);
    this.loadingById.delete(userId);
    this.savingById.delete(userId);
    this.errorById.delete(userId);
    this.editFormsById.delete(userId);
    this.editMetaById.delete(userId);
  };

  clear = () => {
    this.usersById.clear();
    this.loadingById.clear();
    this.savingById.clear();
    this.errorById.clear();
    this.editFormsById.clear();
    this.editMetaById.clear();
  };

  loadManagedUser = async (userId, options = {}) => {
    if (!userId) return null;

    const { force = false } = options;
    const cached = this.usersById.get(userId);
    if (!force && cached) {
      this.applyUserToForm(userId, cached);
      return cached;
    }

    this.loadingById.set(userId, true);
    this.errorById.delete(userId);
    try {
      const data = await userService.getUser(userId, { throwOnError: false });
      if (!data) {
        throw new Error("Unable to load user details.");
      }

      const payload = resolvePayload(data);
      const user = payload?.data || payload || null;

      runInAction(() => {
        if (user) {
          this.usersById.set(userId, user);
          this.applyUserToForm(userId, user);
        } else {
          this.usersById.delete(userId);
          this.editFormsById.delete(userId);
          this.editMetaById.delete(userId);
        }
        this.errorById.delete(userId);
      });

      return user;
    } catch (error) {
      const message = error?.message || "Unable to load user details.";
      runInAction(() => {
        this.usersById.delete(userId);
        this.editFormsById.delete(userId);
        this.editMetaById.delete(userId);
        this.errorById.set(userId, message);
      });
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.loadingById.set(userId, false);
      });
    }
  };

  updateManagedUser = async (userId, payload, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) {
      throw new Error("User ID is required to update user details.");
    }
    this.savingById.set(userId, true);
    try {
      const actionPromise = userService.updateUser(userId, payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving user...",
          success: "User updated successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;

      const payloadData = resolvePayload(data);
      const user = payloadData?.data || payloadData || null;
      runInAction(() => {
        if (user) {
          this.usersById.set(userId, user);
          this.applyUserToForm(userId, user);
        }
        this.errorById.delete(userId);
      });
      return user;
    } catch (error) {
      const message = error?.message || "Unable to update user.";
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.savingById.set(userId, false);
      });
    }
  };

  createManagedUser = async (payload, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    this.createLoading = true;
    this.createError = "";
    try {
      const actionPromise = userService.createUser(payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Creating user...",
          success: "User created successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;

      const payloadData = resolvePayload(data);
      const created = payloadData?.data || payloadData || null;
      runInAction(() => {
        this.resetCreateForm();
      });
      return created;
    } catch (error) {
      const message = error?.message || "Unable to create user.";
      runInAction(() => {
        this.createError = message;
      });
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.createLoading = false;
      });
    }
  };

  assignFollowUp = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.followUpUserId = userId;
    });
    try {
      const actionPromise = userService.assignFollowUp(userId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Updating follow-up...",
          success: "Follow-up updated.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.followUpUserId = null;
      });
    }
  };

  revokeFollowUp = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.followUpUserId = userId;
    });
    try {
      const actionPromise = userService.revokeFollowUp(userId, {
        showErrorToast: false,
      });
      if (shouldToast) {
        showToast.promise(
          actionPromise.then((result) => (result?.success ? true : false)),
          {
            loading: "Updating follow-up...",
            success: "Unfollow-up updated.",
            error: "Unable to remove follow-up.",
            ...toastMessages,
          },
        );
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.followUpUserId = null;
      });
    }
  };

  markUserDeleted = async (userId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    if (!userId) return { success: false };
    runInAction(() => {
      this.deleteLoading = true;
    });
    try {
      const actionPromise = userService.markUserDeleted(userId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Deleting user...",
          success: "User deleted successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }
      return await actionPromise;
    } finally {
      runInAction(() => {
        this.deleteLoading = false;
      });
    }
  };
}

export const userStore = new UserStore();
